import {useConversationCounts} from '@/components/_domain/SidebarItemBadges/ConversationsBadge/ConversationCountsContext';
import {createSelectionStore, SelectionStore} from '@/components/bulkSelection';
import {NetworkStatus, useApolloClient} from '@apollo/client';
import {edgesToNodes} from '@ark/react';
import {useUserProfile, useSelectedCommunities} from '@ark/web';
import {DateTime} from 'luxon';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useParams, useSearchParams} from 'react-router-dom';

import {useBlockFilter} from '@/components/filters/BlockFilter/useBlockFilter';
import {useSubscribeToNotifications} from '@/hooks/useInitializeNotifications';

import {deriveActiveFilters} from './conversationFilterBlocks';

import {
  CommunityTeamMemberFragment,
  ConversationListItemLightFragment,
  ListConversationsLightQuery,
  useCommunityTeamMembersBatchQuery,
  useListConversationsLightQuery,
} from '@/generated/graphql';

export type ConversationFilter =
  | 'all'
  | 'needsResponse'
  | 'assigned'
  | 'assignedToMyTeam'
  | 'unassigned'
  | 'closed'
  | 'snoozed'
  | 'fromEmail'
  | 'fromArk'
  | 'inactive';
export type ConversationsSortOrder =
  | 'last_event_time'
  | '-last_event_time'
  | 'inactive_oldest'
  | null;

export type TeamMember = CommunityTeamMemberFragment;

type ConversationsPageContextType = {
  search?: string;
  setSearch: (value?: string) => void;
  dateFrom: DateTime | null;
  setDateFrom: (date: DateTime | null) => void;
  dateTo: DateTime | null;
  setDateTo: (date: DateTime | null) => void;
  sortOrder: ConversationsSortOrder;
  setSortOrder: (order: ConversationsSortOrder) => void;
  conversationFilter: ConversationFilter;
  selectedUsers: string[];
  setSelectedUsers: (users: string[] | ((prev: string[]) => string[])) => void;
  selectedTags: string[];
  setSelectedTags: (tags: string[] | ((prev: string[]) => string[])) => void;
  block?: string;

  conversations: ConversationListItemLightFragment[];
  loading: boolean;
  isInitialLoading: boolean;
  refetch: () => void;
  data: ListConversationsLightQuery | undefined;
  fetchMore: ({variables}: {variables: {after?: string | null}}) => void;
  networkStatus: NetworkStatus;

  selectedConversationId?: string;

  selectionMode: boolean;
  selectionStore: SelectionStore;
  selectAllMatchingFilter: boolean;
  setSelectAllMatchingFilter: (value: boolean) => void;
  totalMatchingFilter: number;

  assignedToValue?: string | null;
  teamsValue?: string[];
  sourceValue?: string[];
  participantTypeValue?: string[];

  communityTeamMembersMap: Record<string, readonly TeamMember[]>;
  allTeamMembers: TeamMember[];
  teamMembersLoading: boolean;
};

const ConversationsPageContext = createContext<
  ConversationsPageContextType | undefined
>(undefined);

type ConversationsPageProviderProps = {
  children: React.ReactNode;
};

export const ConversationsPageProvider = ({
  children,
}: ConversationsPageProviderProps) => {
  const apolloClient = useApolloClient();
  const {id: userId, teamIds} = useUserProfile();
  const {conversationId} = useParams<{conversationId: string}>();
  const [searchParams] = useSearchParams();
  const communities = useSelectedCommunities();
  const block = useBlockFilter().selectedBlock?.id;

  const activeFilters = useMemo(
    () => deriveActiveFilters(searchParams.getAll('filter')),
    [searchParams],
  );
  const conversationFilter: ConversationFilter =
    (activeFilters.status as ConversationFilter | undefined) ?? 'all';

  const [search, setSearch] = useState<string | undefined>();
  const {
    dateFrom,
    dateTo,
    setDateFrom,
    setDateTo,
    selectedTags,
    setSelectedTags,
    selectedUsers,
    setSelectedUsers,
  } = useConversationCounts();
  const [sortOrder, setSortOrder] =
    useState<ConversationsSortOrder>('-last_event_time');

  useSubscribeToNotifications(notification => {
    if (notification?.target?.__typename === 'ConversationNode') {
      apolloClient.refetchQueries({
        include: ['listConversationsLight'],
      });
    }
  }, 'ConversationList');

  const {data: teamMembersData, loading: teamMembersLoading} =
    useCommunityTeamMembersBatchQuery({
      variables: {communityIds: communities},
      skip: communities.length === 0,
    });

  const communityTeamMembersMap = useMemo(() => {
    const groups = teamMembersData?.communityTeamMembersBulk ?? [];
    const map: Record<string, readonly CommunityTeamMemberFragment[]> = {};
    for (const group of groups) {
      map[group.communityId] = group.members;
    }
    return map;
  }, [teamMembersData]);

  const allTeamMembers = useMemo(() => {
    const seen = new Set<string>();
    const result: TeamMember[] = [];
    for (const members of Object.values(communityTeamMembersMap)) {
      for (const member of members) {
        if (!seen.has(member.id)) {
          seen.add(member.id);
          result.push(member);
        }
      }
    }
    return result;
  }, [communityTeamMembersMap]);

  const communityKey = [...communities].sort().join(',');
  const initializedForCommunitiesRef = useRef('');

  useEffect(() => {
    if (teamMembersLoading) return;

    if (communities.length === 0) {
      if (initializedForCommunitiesRef.current !== '') {
        initializedForCommunitiesRef.current = '';
      }
      return;
    }

    if (initializedForCommunitiesRef.current !== communityKey) {
      initializedForCommunitiesRef.current = communityKey;
      setSelectedUsers(['Unassigned', ...allTeamMembers.map(m => m.id)]);
    }
  }, [
    teamMembersLoading,
    communityKey,
    communities.length,
    allTeamMembers,
    setSelectedUsers,
  ]);

  const assignedToValue = useMemo(() => {
    if (activeFilters.assignment === 'assigned') return userId;
    if (activeFilters.assignment === 'unassigned') return 'Unassigned';
    return undefined;
  }, [activeFilters.assignment, userId]);

  const statusFilter = useMemo(() => {
    if (activeFilters.status === 'closed') return ['CL'];
    if (activeFilters.status === 'snoozed') return ['SN'];
    if (activeFilters.status === 'all') return ['OP', 'SN'];
    return ['OP'];
  }, [activeFilters.status]);

  const unansweredValue = useMemo(() => {
    if (activeFilters.status === 'needsResponse') return ['unanswered'];
    return undefined;
  }, [activeFilters.status]);

  const teamsValue = useMemo(() => {
    if (activeFilters.assignment === 'assignedToMyTeam') {
      return teamIds?.length ? teamIds : [];
    }
    return undefined;
  }, [activeFilters.assignment, teamIds]);

  const sourceValue = useMemo(() => {
    if (activeFilters.source === 'fromEmail') return ['EMAIL'];
    if (activeFilters.source === 'fromArk') return ['ARK'];
    return undefined;
  }, [activeFilters.source]);

  const participantTypeValue = useMemo(() => {
    if (activeFilters.sender) return [activeFilters.sender];
    return undefined;
  }, [activeFilters.sender]);

  const clientTagsValue = useMemo(() => {
    if (selectedTags.length > 0) return selectedTags;
    return undefined;
  }, [selectedTags]);

  const isInactive = conversationFilter === 'inactive';

  const selectionStoreRef = useRef<SelectionStore | null>(null);
  if (!selectionStoreRef.current) {
    selectionStoreRef.current = createSelectionStore();
  }
  const selectionStore = selectionStoreRef.current;
  const [selectAllMatchingFilter, setSelectAllMatchingFilter] = useState(false);

  useEffect(() => {
    selectionStore.clear();
    setSelectAllMatchingFilter(false);
  }, [
    communityKey,
    conversationFilter,
    activeFilters.assignment,
    activeFilters.source,
    activeFilters.sender,
    dateFrom,
    dateTo,
    isInactive,
    search,
    selectedTags,
    selectedUsers,
    selectionStore,
    block,
  ]);

  const assignedUsers = useMemo(() => {
    if (
      activeFilters.assignment === 'assigned' ||
      activeFilters.assignment === 'unassigned'
    ) {
      return undefined;
    }
    if (selectedUsers.length > 0) {
      return selectedUsers;
    }
    return undefined;
  }, [selectedUsers, activeFilters.assignment]);

  const effectiveSortOrder = isInactive
    ? 'inactive_oldest'
    : sortOrder || '-last_event_time';

  const {data, loading, fetchMore, refetch, networkStatus} =
    useListConversationsLightQuery({
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
      skip:
        communities.length > 0 &&
        initializedForCommunitiesRef.current !== communityKey,
      variables: {
        first: 15,
        search,
        orderBy: effectiveSortOrder,
        communities,
        status: statusFilter,
        assignedTo: assignedToValue,
        dateFrom: dateFrom?.isValid ? dateFrom.toISODate() : undefined,
        dateTo: dateTo?.isValid ? dateTo.toISODate() : undefined,
        assignedUsers,
        unanswered: unansweredValue,
        teams: teamsValue,
        source: sourceValue,
        participantType: participantTypeValue,
        clientTags: clientTagsValue,
        inactive: isInactive || undefined,
        block,
      },
    });

  const isInitialLoading = loading && !data;
  const conversations = useMemo(
    () =>
      edgesToNodes(
        data?.conversations?.edges?.filter(
          (edge, i, arr) =>
            arr.findIndex(e => e?.node?.id === edge?.node?.id) === i,
        ),
      ),
    [data],
  );

  const value: ConversationsPageContextType = {
    search,
    setSearch,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    sortOrder: effectiveSortOrder,
    setSortOrder,
    conversationFilter,
    selectedUsers,
    setSelectedUsers,
    selectedTags,
    setSelectedTags,
    block,

    conversations,
    loading,
    isInitialLoading,
    refetch,
    data,
    fetchMore,
    networkStatus,

    selectedConversationId: conversationId,

    selectionMode: conversationFilter !== 'closed',
    selectionStore,
    selectAllMatchingFilter,
    setSelectAllMatchingFilter,
    totalMatchingFilter: data?.conversations?.totalCount ?? 0,

    assignedToValue,
    teamsValue,
    sourceValue,
    participantTypeValue,

    communityTeamMembersMap,
    allTeamMembers,
    teamMembersLoading,
  };

  return (
    <ConversationsPageContext.Provider value={value}>
      {children}
    </ConversationsPageContext.Provider>
  );
};

export const useConversationsPage = () => {
  const context = useContext(ConversationsPageContext);
  if (!context) {
    throw new Error(
      'useConversationsPage must be used within a ConversationsPageProvider',
    );
  }
  return context;
};
